import React, { createContext, useContext, useRef, useMemo } from 'react';
import { createEventStore } from './EventsStore';
import { createAppStore } from './AppStore';
import { createPlayerStore } from './PlayerStore';
import { createCheckinStore } from './CheckinStore';
import { createExamSessionStore } from './ExamSessionStore';
import { createVideoFragmentStore } from './VideoFragmentStore';
import { createLayoutStore } from './LayoutStore';
import { createCommunicationStore } from './CommunicationStore';
import { createSettingsStore } from './SettingsStore';

const StoreContext = createContext(null);

export const StoreProvider = ({ children, initialState = {} }) => {
  // create store instances and inject dependencies using useMemo to ensure it only happens once
  // but synchronously before render (avoiding the "setState during render" issue)
  const stores = useMemo(() => {
    // create store instances with placeholder deps
    const appStore = createAppStore(initialState.app);
    const layoutStore = createLayoutStore(initialState.layout);
    const eventStore = createEventStore(initialState.events);
    const playerStore = createPlayerStore(initialState.player);
    const checkinStore = createCheckinStore(initialState.checkin);
    const examSessionStore = createExamSessionStore(initialState.examSession);
    const videoFragmentStore = createVideoFragmentStore(initialState.videoFragment);
    const communicationStore = createCommunicationStore(initialState.communication);
    const settingsStore = createSettingsStore(initialState.settings);

    // inject all the dependencies synchronously
    appStore
      .getState()
      .injectDependentStores({ playerStore, examSessionStore, eventStore, communicationStore });
    playerStore.getState().injectDependentStores({ examSessionStore, appStore });
    layoutStore
      .getState()
      .injectDependentStores({ checkinStore, appStore, examSessionStore });
    checkinStore.getState().injectDependentStores({ layoutStore, eventStore, appStore, examSessionStore });
    examSessionStore.getState().injectDependentStores({ playerStore, appStore, eventStore, communicationStore });
    eventStore.getState().injectDependentStores({ appStore, examSessionStore, communicationStore });
    videoFragmentStore
      .getState()
      .injectDependentStores({ appStore, examSessionStore });
    communicationStore.getState().injectDependentStores({ appStore, examSessionStore, layoutStore, eventStore });
    settingsStore.getState().injectDependentStores({ eventStore });

    return {
      appStore,
      layoutStore,
      eventStore,
      playerStore,
      checkinStore,
      examSessionStore,
      videoFragmentStore,
      communicationStore,
      settingsStore,
    };
  }, []); // Empty dependency array - only create stores once

  return (
    <StoreContext.Provider value={stores}>{children}</StoreContext.Provider>
  );
};

export const useStores = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStores must be used inside StoreProvider');
  return ctx;
};

const withStoreSelector = (storeKey) => (selector, equals) => {
  const ctx = useStores();
  return ctx[storeKey](selector, equals);
};

const withRawStore = (storeKey) => () => {
  const ctx = useStores();
  return ctx[storeKey];
};

export const getStoreRaw = (storeKey) => {
  const context = useContext(StoreContext);
  if (!context)
    throw new Error('useScopedStoreRef must be used inside StoreProvider');
  return context[storeKey]; // this is the Zustand store instance
};

export const getStore = (storeKey) => {
  const context = useContext(StoreContext);
  if (!context)
    throw new Error('useScopedStoreRef must be used inside StoreProvider');
  return context[storeKey].getState(); // this is the Zustand store instance
};

// Hook-style selectors
export const useAppStore = withStoreSelector('appStore');
export const useLayoutStore = withStoreSelector('layoutStore');
export const useEventStore = withStoreSelector('eventStore');
export const usePlayerStore = withStoreSelector('playerStore');
export const useCheckinStore = withStoreSelector('checkinStore');
export const useExamSessionStore = withStoreSelector('examSessionStore');
export const useVideoFragmentStore = withStoreSelector('videoFragmentStore');
export const useCommunicationStore = withStoreSelector('communicationStore');
export const useSettingsStore = withStoreSelector('settingsStore');

// Raw store access
export const getAppStore = withRawStore('appStore');
export const getLayoutStore = withRawStore('layoutStore');
export const getEventStore = withRawStore('eventStore');
export const getPlayerStore = withRawStore('playerStore');
export const getCheckinStore = withRawStore('checkinStore');
export const getExamSessionStore = withRawStore('examSessionStore');
export const getVideoFragmentStore = withRawStore('videoFragmentStore');
export const getCommunicationStore = withRawStore('communicationStore');
export const getSettingsStore = withRawStore('settingsStore');

